import React, { Component } from 'react';
import { View, Keyboard, ScrollView } from 'react-native';
// import RNRestart from 'react-native-restart';
import * as Updates from 'expo-updates';

import Alert from './shared/Alert';
import Button from './shared/Button';
import { TextInputLabel } from './shared/TextInput';
import Modal from './shared/Modal';
import Picker from './shared/Picker';

import Footer from './common/Footer';

import heroStore from '../stores/hero';
import appStore from '../stores/app';
import authStore, { SIGN_IN_TYPE_FB } from '../stores/auth';
import { isQuick } from '../utils/warriorUtils';
import { signOut } from './App';
import { checkOldPassword, updateEmail, updatePassword } from '../lib/userApiCalls';
import { deleteAccount } from '../lib/apiCalls';

import { EMAIL_PATTERN } from './SignUpModal';

import { t, getLng, setToStorage } from '../utils/appUtils';
import { IS_WEB } from '../lib/appConstants';
import { pOrT, MARGIN_10, MARGIN_20, BUTTON_HEIGHT } from './styleConstants';

import log from '../lib/log';

type FieldValueProps = {
  value: string;
  label: string;
  hint: string;
  type: 'EMAIL' | 'TEXT' | 'TEXTAREA' | 'PASSWORD' | 'SELECT' | 'NUMBER';
  values?: { key: string | number; label: string }[];
  onChange: Function;
  onBlur?: Function;
};

export class FieldValue extends Component<FieldValueProps> {
  static propTypes = {};

  onChangeValue = (value: any) => {
    const { onChange } = this.props;
    onChange(value);
  };

  onBlur = () => {
    const { onBlur } = this.props;
    if (onBlur) onBlur();
  };

  render() {
    const { type, values, value, label, hint } = this.props;

    switch (type) {
      case 'EMAIL':
      case 'TEXT':
      case 'NUMBER':
      case 'PASSWORD':
      case 'TEXTAREA':
        return (
          <TextInputLabel
            label={label}
            hint={hint}
            autoCapitalize="none"
            onChangeText={this.onChangeValue}
            secureTextEntry={type === 'PASSWORD'}
            onBlur={this.onBlur}
            keyboardType={type === 'NUMBER' ? 'numeric' : undefined}
            value={value}
            returnKeyType={type === 'NUMBER' ? 'done' : undefined}
            multiline={type === 'TEXTAREA'}
          />
        );
      case 'SELECT':
        return (
          <Picker
            data={values!}
            label={label}
            hint={hint}
            value={value}
            onChange={this.onChangeValue}
          />
        );
      default:
        return null;
    }
  }
}

type State = {
  fields: {
    name: string;
    email: string;
    newPassword: string;
    oldPassword: string;
    about: string;
    lng: string;
  };
};

type SettingsModalProps = {
  onHide?: Function;
};

export default class SettingsModal extends Component<SettingsModalProps, State> {
  state: State = {
    fields: {
      name: heroStore.hero.name || '',
      email: authStore.user.email,
      oldPassword: '',
      newPassword: '',
      about: heroStore.hero.about || '',
      lng: 'en',
    },
  };

  componentDidMount() {
    this.setState((state) => ({
      fields: {
        ...state.fields,

        lng: getLng(),
      },
    }));
  }

  onHide = () => {
    this.props.onHide!();
  };

  onDeleteAccount = () => {
    Alert.alert(t('settings.deleteAccountConfirmTitle'), t('settings.deleteAccountConfirmMsg'), [
      { text: t('cancel'), style: 'cancel' },
      {
        text: t('settings.deleteAccount'),
        style: 'destructive',
        onPress: this.confirmDeleteAccount,
      },
    ]);
  };

  confirmDeleteAccount = async () => {
    appStore.toggleLoading(true);
    try {
      await deleteAccount();
    } catch (e) {
      appStore.toggleLoading(false);
      appStore.showMessage({ type: 'error', text: (e as any).message });
      return;
    }
    appStore.toggleLoading(false);
    await signOut();
  };

  onSave = async () => {
    const validateMsg = await this.validate();

    if (validateMsg) {
      appStore.showMessage({ type: 'error', text: validateMsg });
      return;
    }

    const { fields } = this.state;
    const { name, email, newPassword, about, lng } = fields;

    await heroStore.saveGeneral({
      name,
      about,
      lng: lng as string,
    });

    (log as any)('saveSettingsAction');

    try {
      if (email !== authStore.user.email) {
        await updateEmail(authStore.user, email);
      }
      if (newPassword) {
        await updatePassword(authStore.user, newPassword);

        this.setState((state) => ({
          fields: {
            ...state.fields,
            oldPassword: '',
            newPassword: '',
          },
        }));
      }
    } catch (e) {
      appStore.showMessage({ type: 'error', text: (e as any).message });
      return;
    }

    const oldLngValue = getLng();
    let restart = false;
    if (oldLngValue !== lng) {
      await setToStorage('lng', lng);
      if (!IS_WEB) {
        setTimeout(this.onHide, 1000);
      }
      setTimeout(() => {
        if (!IS_WEB) {
          // RNRestart.Restart();
          Updates.reloadAsync();
        } else {
          window.location.reload();
        }
      }, 1500);
      restart = true;
    }

    Keyboard.dismiss();

    appStore.showMessage({
      type: 'success',
      text: !restart ? t('settings.dataSaved') : t('settings.dataSavedWithRestart'),
    });
  };

  async validate() {
    const { fields } = this.state;
    const { email, oldPassword, newPassword } = fields;

    if (!EMAIL_PATTERN.test(email)) return t('auth.emailInvalid');
    if (newPassword) {
      if (!oldPassword) return t('setting.oldPasswordSpecify');

      if (!(await checkOldPassword(authStore.user.email, oldPassword)))
        return t('settings.wrongOldPassword');
    }

    return null;
  }

  render() {
    const { fields } = this.state;

    return (
      <Modal
        size={pOrT(
          {
            width: 620,
            height: 340,
          },
          {
            width: 620,
            height: 540,
          },
        )}
        onHide={this.onHide}
        onOk={this.onSave}
        onOkNext={() => {
          if (isQuick(heroStore.hero)) {
            Alert.alert(t('auth.signOutNotSavedProgress'), t('areYouSure'), [
              { text: t('cancel'), style: 'cancel' },
              { text: t('yes'), onPress: () => signOut() },
            ]);
          } else {
            signOut();
          }
        }}
        okLabel={t('settings.save').toUpperCase()}
        okNextLabel={t('menu.logout').toUpperCase()}
        onOkNextProps={{ testID: 'settings.logout', variant: 'red' }}
        title={t('settings.title')}
      >
        <View
          style={{
            marginTop: 40,
            alignItems: 'center',
          }}
        >
          <ScrollView
            style={{
              height: IS_WEB ? 440 : pOrT(210, 390),
              width: 480,
            }}
            contentContainerStyle={{
              alignItems: 'center',
              paddingBottom: IS_WEB ? 0 : pOrT(100, 200),
            }}
          >
            {[
              {
                name: 'name',
                type: 'TEXT',
              },
              {
                name: 'email',
                type: 'EMAIL',
              },
              {
                name: 'oldPassword',
                type: 'PASSWORD',
              },
              {
                name: 'newPassword',
                type: 'PASSWORD',
              },
              {
                name: 'about',
                type: 'TEXTAREA',
              },
              {
                name: 'lng',
                type: 'SELECT',
                hint: t('settings.needRestart'),
                values: [
                  { key: 'en', label: t('english') },
                  { key: 'es', label: t('spanish') },
                  { key: 'pt', label: t('portuguese') },
                  { key: 'zh', label: t('chinese') },
                  { key: 'ja', label: t('japanese') },
                  { key: 'ko', label: t('korean') },
                  { key: 'ru', label: t('russian') },
                  { key: 'fr', label: t('french') },
                  { key: 'de', label: t('german') },
                  { key: 'it', label: t('italian') },
                ],
              },
            ]
              .filter(
                (item) =>
                  authStore.getProvider() !== SIGN_IN_TYPE_FB ||
                  (authStore.getProvider() === SIGN_IN_TYPE_FB &&
                    !['oldPassword', 'newPassword', 'email'].includes(item.name)),
              )
              .filter(
                (item) =>
                  !isQuick(heroStore.hero) ||
                  (isQuick(heroStore.hero) && ['lng', 'about', 'name'].includes(item.name)),
              )
              .map((item: any) => (
                <View key={item.name} style={{ marginBottom: MARGIN_10 }}>
                  <FieldValue
                    value={(fields as any)[item.name]}
                    label={t(`settings.fields.${item.name}`).toUpperCase()}
                    hint={item.hint}
                    type={item.type}
                    values={item.values}
                    onChange={(value: any) => {
                      this.setState((state) => ({
                        fields: {
                          ...state.fields,
                          [item.name]: value,
                        },
                      }));
                    }}
                  />
                </View>
              ))}
            <View
              style={{
                // The language Picker collapses to 0 layout height on web (its
                // contents are absolutely positioned), so clear its 55px inputBar.
                marginTop: IS_WEB ? 55 + MARGIN_20 : MARGIN_20,
                alignItems: 'center',
              }}
            >
              <Button
                variant="red"
                onPress={this.onDeleteAccount}
                testID="settings.deleteAccount"
                sizeObj={{ width: 200, height: BUTTON_HEIGHT }}
              >
                {t('settings.deleteAccount').toUpperCase()}
              </Button>
            </View>
          </ScrollView>
        </View>
        {!IS_WEB && (
          <View
            style={{
              width: '100%',
              position: 'absolute',
              bottom: MARGIN_20,
            }}
          >
            <Footer divide />
          </View>
        )}
      </Modal>
    );
  }
}
